import { AnimationEvent } from '@angular/animations';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import {
  ChoiceValidationResult,
  IngredientChoice,
  OrderableIngredient,
  OrderedChoice,
  OrderedChoiceService,
  OrderableIngredientGroup,
  AuditService,
  OrderedItem,
  OrderedIngredient,
  StoreConfig,
  ConfigService,
  Tag,
  Utils,
} from 'ngx-web-api';
import { Subscription } from 'rxjs';
import { Animations } from '../../../animations';
import { ThemeService } from '../../../core/services/theme.service';
import { IngredientSelection } from '../../../domain/ingredient-selection';
import { Theme } from '../../../domain/theme.model';
import { UiOrchestratorService } from '../../../core/services/ui-orchestrator.service';

import { StageIngredientAction } from '../../../item-editor/domain/stage-ingredient-action.model';
import { IngredientAction } from '../../../item-editor/domain/ingredient-action.enum';
import { ViewportService } from '../../../core/services/viewport.service';
import { ViewPort } from '../../../domain/view-port.model';
import { distinctUntilChanged } from 'rxjs/operators';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { ModalService } from 'app/core/services/modal.service';
import { TranslateService } from '@ngx-translate/core';
import { EditingItemChoicesService } from 'app/core/services/editing-item-choices.service';
import { IngredientGroupFilterPipe } from 'app/shared/pipes/ingredient-group-filter.pipe';
import { SlugifyPipe } from 'app/shared/pipes/slugify.pipe';
import { HalfsOrderingService } from 'app/core/services/halfs-ordering.service';

@Component({
  selector: 'fts-choice',
  templateUrl: './choice.component.html',
  styleUrls: ['./choice.component.scss'],
  animations: [Animations.scaleX, Animations.expanded],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChoiceComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  item: OrderedItem;
  @Input()
  isInModal: boolean;
  @Input()
  choice: IngredientChoice;
  @Input()
  orderedChoice: OrderedChoice;
  @Input()
  itemSize: string;
  @Input()
  tabbed = true;
  @Input()
  isDependingIngredientOrdered: boolean;
  @Input()
  showValidationMessages = true;
  @Input()
  useShortValidationMessages = false;
  @Input()
  caloriesDisclaimer?: string;
  @Input()
  hasGraphicalBuilder: boolean;
  @Input()
  ignoreIngredientGroups: boolean;
  @Input()
  isSelected = false;
  @Input()
  hasChoiceGroups: boolean;
  @Input()
  inItemEditorModal = false;
  @Input()
  isChoiceChanged: boolean;

  @Output()
  chooseHalf: EventEmitter<IngredientSelection> = new EventEmitter();
  @Output()
  toggleIngredients: EventEmitter<IngredientSelection[]> = new EventEmitter();
  @Output()
  toggleQualifier: EventEmitter<IngredientSelection> = new EventEmitter();
  @Output()
  stageAction: EventEmitter<StageIngredientAction> = new EventEmitter();

  faAngleRight = faAngleRight;
  expanded = false;
  collapsedChoice = true;
  theme: Theme;
  selectedIngredientGroup: OrderableIngredientGroup;
  isInTablet = false;
  isMobile = false;
  showThreeColumns = false;
  maxIngredientsWarningMessage: string;
  private subscriptions: Subscription[];
  needsMaxIngredientsWarningsCheck: boolean;
  storeConfig: StoreConfig;
  tagList: Tag[];
  selectedTags: string[] = [];
  filteredIngredient: OrderableIngredient[];
  private ingredientGroupFilterPipe = new IngredientGroupFilterPipe();
  private slugifyPipe = new SlugifyPipe();
  selectedChoiceGroupName: string;
  isHalfAndHalf = false;

  constructor(
    private themeService: ThemeService,
    public orderedChoiceService: OrderedChoiceService,
    private auditService: AuditService,
    private viewPortService: ViewportService,
    private uiOrchestrationService: UiOrchestratorService,
    private liveAnnouncer: LiveAnnouncer,
    private modalService: ModalService,
    private translateService: TranslateService,
    private editingService: EditingItemChoicesService,
    private cdRef: ChangeDetectorRef,
    private configService: ConfigService,
    public halfsOrderingService: HalfsOrderingService
  ) {
    this.subscriptions = [
      this.themeService.theme.subscribe(theme => (this.theme = theme)),
      this.halfsOrderingService.isHalfAndHalf$.subscribe(v => (this.isHalfAndHalf = v)),
    ];
    this.storeConfig = this.configService.storeConfig.getValue();
  }

  ngOnInit(): void {
    this.validateOrderedChoice();
    this.collapsedChoice = !this.tabbed && !this.isSelected;
    this.expanded = !this.collapsedChoice || !!this.orderedChoice?.warnings.length || this.choice.isOpenOnWeb;
    this.viewPortService.viewport$.pipe(distinctUntilChanged()).subscribe((viewport: ViewPort) => {
      this.isMobile = viewport.isMobile;
      this.isInTablet = viewport.isTablet;
      this.calculateColumms();
    });

    this.subscriptions.push(
      this.editingService.visibleChoice$.subscribe(visible => {
        if (!this.tabbed) {
          if (visible?.name === this.choice?.name && !this.expanded) {
            this.toggleExpanded();
            this.cdRef.detectChanges();
          }
        }
      }),
      this.editingService.visibleChoiceGroup$.subscribe(visibleChoiceGroup => {
        if (visibleChoiceGroup?.name != this.selectedChoiceGroupName) {
          this.selectedChoiceGroupName = visibleChoiceGroup?.name;
          this.initTagList(this.ingredientGroupFilterPipe.transform(this.choice?.ingredients, this.selectedIngredientGroup?.ingredients));
        }
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['choice']) {
      this.needsMaxIngredientsWarningsCheck = false;
      this.selectedIngredientGroup = this.choice.groups && this.choice.groups.length ? this.choice.groups[0] : undefined;
      this.initTagList(this.ingredientGroupFilterPipe.transform(this.choice?.ingredients, this.selectedIngredientGroup?.ingredients));
    }

    if (changes['choice'] || changes['isInModal']) {
      this.calculateColumms();
    }

    if (changes['orderedChoice']) {
      if (!this.choice.needsSingleIngredientSelection && this.choice.maxIngredients) {
        this.needsMaxIngredientsWarningsCheck = this.choice.maxIngredients === this.orderedChoice.ingredients.length;
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  selectHalf(selection: IngredientSelection) {
    this.chooseHalf.emit(Object.assign({ choice: this.choice }, selection) as IngredientSelection);
    const orderedIngredient = this.orderedChoice.ingredients.find(i => i.ingredient === selection.ingredient.name);

    const orderedQualifiers =
      !!orderedIngredient.qualifiers && orderedIngredient.qualifiers.map(q => selection.ingredient.qualifiers.find(n => n.name === q));

    this.emitBuilderStageAction({
      orderableIngredient: selection.ingredient,
      action: IngredientAction.SELECT_HALF,
      left: selection.isLeftHalf,
      right: selection.isRightHalf,
      qualifier: !!orderedQualifiers && orderedQualifiers.find(q => q.recipeFactor !== 0),
    });
    this.validateOrderedChoice();
  }

  onToggleQualifier(selection: IngredientSelection) {
    this.toggleQualifier.emit(Object.assign({ choice: this.choice }, selection) as IngredientSelection);
    const orderedIngredient = this.orderedChoice.ingredients.find(i => i.ingredient === selection.ingredient.name);
    const shouldAdd = orderedIngredient?.qualifiers && orderedIngredient?.qualifiers.indexOf(selection.qualifier.name) !== -1;
    const secondShouldAdd =
      orderedIngredient?.secondHalfQualifiers && orderedIngredient?.secondHalfQualifiers.indexOf(selection.qualifier.name) !== -1;
    this.emitBuilderStageAction({
      orderableIngredient: selection.ingredient,
      action: shouldAdd ? IngredientAction.ADD_QUALIFIER : IngredientAction.REMOVE_QUALIFIER,
      qualifier: selection.qualifier,
      orderedQualifierList: orderedIngredient.qualifiers,
      left: orderedIngredient.isLeftHalf,
      right: orderedIngredient.isRightHalf,
    });

    if (selection.isSecondHalf) {
      this.liveAnnouncer.announce(
        `Second qualifier, ${selection?.qualifier.name}, has been ${secondShouldAdd ? 'added' : 'removed'}`,
        2000
      );
    } else {
      this.liveAnnouncer.announce(`Qualifier, ${selection?.qualifier.name}, has been ${shouldAdd ? 'added' : 'removed'}`, 2000);
    }
  }

  onToggleIngredient(ingredient: OrderableIngredient) {
    const ingredients: IngredientSelection[] = [{ choice: this.choice, ingredient }];
    const addingIngredient = !(this.item && this.item.hasIngredient(ingredients[0].choice, ingredients[0].ingredient));
    if (this.needsMaxIngredientsWarningsCheck && addingIngredient) {
      this.validateOrderedChoice(ingredient);
    } else {
      if (this.isHalfAndHalf && ingredient.allowHalves) {
        this.halfsOrderingService.addOrRemoveIngredientToHalfOrderedItem(ingredient, this.choice.name);
      } else {
        this.toggleIngredients.emit(this.getIngredientsToToggle(ingredient));
        const shouldAdd = this.orderedChoice.ingredients.find(i => i.ingredient === ingredient.name);
        this.emitBuilderStageAction({
          orderableIngredient: ingredient,
          action: shouldAdd ? IngredientAction.ADD : IngredientAction.REMOVE,
        });
        this.validateOrderedChoice();
      }
    }
  }

  toggleExpanded() {
    this.expanded = !this.expanded;
    if (this.expanded) {
      this.auditService.createAudit(() => `Selected "${this.choice.name}" choice`);
    }
  }

  trackByIngredientFn(_index, ingredient: OrderableIngredient) {
    return ingredient.name;
  }

  expandedAnimationStarted(event: AnimationEvent) {
    if (!event.fromState && !!event.toState) {
      this.collapsedChoice = false;
    }
  }

  expandedAnimationEnd(event: AnimationEvent) {
    if (!!event.fromState && !event.toState) {
      this.collapsedChoice = true;
    }

    this.uiOrchestrationService.recalculateStepControlsPosition();
  }

  selectGroup(group: OrderableIngredientGroup, event: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.auditService.createAudit(() => {
      return `Selected "${group.choiceGroup}" group button`;
    });

    this.selectedIngredientGroup = group;
    setTimeout(() => this.uiOrchestrationService.recalculateStepControlsPosition());
    this.initTagList(this.ingredientGroupFilterPipe.transform(this.choice?.ingredients, this.selectedIngredientGroup?.ingredients));
  }

  onTagClicked(tag: string) {
    const tagIndex = this.selectedTags.findIndex(t => t === tag);
    if (tagIndex > -1) {
      this.selectedTags.splice(tagIndex, 1);
      this.selectedTags = [...this.selectedTags];
    } else {
      this.selectedTags = [...this.selectedTags, tag];
    }

    this.auditService.createAudit(
      () => `Tag '${tag}' filtering for choice '${this.choice?.name}' has been ${tagIndex < 0 ? 'activated' : 'deactivated'}`
    );
    if (!!this.choice && (this.isMobile || this.isInTablet)) {
      document
        .getElementById(`choice-${this.slugifyPipe.transform(this.choice.name)}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    this.filterIngredients();
  }

  clearSelectedTags() {
    this.selectedTags = [];
    this.filterIngredients();
  }

  private filterIngredients() {
    if (this.choice) {
      const choiceCopy = this.choice.copy();

      this.filteredIngredient = choiceCopy.ingredients.filter(ingredient => {
        return this.selectedTags.length > 0 ? !!ingredient.tags.find(tag => this.selectedTags.find(t => t === tag.name)) : [];
      });

      this.cdRef.detectChanges();
    }
  }

  private calculateColumms() {
    this.showThreeColumns =
      !this.isInModal && (!this.isInTablet || (this.choice.showIngredientImages && !this.halfsOrderingService.isHalfAndHalf.getValue()));
  }

  private validateOrderedChoice(ingredient?: OrderableIngredient): void {
    if (ingredient) {
      const copiedOrderedChoice = Object.assign({}, this.orderedChoice, {
        ingredients: [...this.orderedChoice.ingredients, OrderedIngredient.createFromOrderable(ingredient)],
      });
      const validationResult: ChoiceValidationResult = this.orderedChoiceService.validateChoice(
        copiedOrderedChoice,
        this.choice,
        this.itemSize,
        this.isHalfAndHalf
      );
      this.orderedChoice.warnings = [];
      this.maxIngredientsWarningMessage = this.useShortValidationMessages ? validationResult.shortMessage : validationResult.longMessage;
      this.modalService.openWarningNotificationModal(
        this.translateService.instant('component.item_editor.choice.item_requirements'),
        this.maxIngredientsWarningMessage
      );
    } else {
      const validationResult: ChoiceValidationResult = this.orderedChoiceService.validateChoice(
        this.orderedChoice,
        this.choice,
        this.itemSize,
        this.isHalfAndHalf
      );
      this.orderedChoice.warnings = validationResult.valid
        ? []
        : [this.useShortValidationMessages ? validationResult.shortMessage : validationResult.longMessage];
    }
  }

  private getIngredientsToToggle(ingredient: OrderableIngredient) {
    const ingredients: IngredientSelection[] = [{ choice: this.choice, ingredient }];

    if (this.choice.needsSingleIngredientSelection || this.choice.maxIngredients === 1) {
      (this.orderedChoice.ingredients || [])
        .filter(i => i.ingredient !== ingredient.name)
        .map(i => ({
          choice: this.choice,
          ingredient: this.choice.ingredients.find(ingr => ingr.name === i.ingredient),
        }))
        .forEach(ingr => ingredients.push(ingr));
    }

    return ingredients;
  }

  private emitBuilderStageAction(action: StageIngredientAction) {
    if (this.hasGraphicalBuilder) {
      this.stageAction.emit(action);
    }
  }

  private initTagList(orderableIngredient: OrderableIngredient[]) {
    this.tagList = Utils.flatten(orderableIngredient.map(ingredient => ingredient.tags)).reduce((a, b) => {
      const tagIncldued = a.find(t => t.name === b.name);
      return tagIncldued ? a : [...a, b];
    }, []);
    this.selectedTags = [];
    this.filterIngredients();
  }
}
